/**
 * Deferred-init scheduler for non-critical telemetry/analytics SDKs.
 *
 * Boot→interactive is the most latency-sensitive window of the app — on the
 * desktop webview (WKWebView/WebView2) especially, every SDK that initializes
 * at module scope competes with hydration for the main thread. SDKs whose
 * pre-init calls are buffered (Amplitude, Segment) or that backfill the
 * initial view from buffered performance entries (Datadog RUM) can safely
 * initialize *after* the page is interactive instead.
 *
 * Scheduling: wait for the window `load` event (or run immediately if it
 * already fired), then wait for an idle period via `requestIdleCallback`.
 * WKWebView/Safari shipped `requestIdleCallback` only recently, so fall back
 * to a short `setTimeout` when it's unavailable. The rIC `timeout` bounds the
 * worst case so telemetry is never delayed indefinitely on busy pages.
 *
 * NOT for boot-critical SDKs: PostHog/Flagsmith gate first-paint UI via
 * feature flags, and Snowplow (primary analytics destination) silently drops
 * events tracked before its tracker exists — those stay synchronous.
 */

const IDLE_CALLBACK_TIMEOUT_MS = 3000;
const NO_IDLE_CALLBACK_FALLBACK_DELAY_MS = 1500;

type TelemetryTask = () => void;

const runGuarded = (task: TelemetryTask): void => {
  try {
    task();
  } catch (error) {
    // Telemetry init must never take the app down; surface for debugging only.
    // eslint-disable-next-line no-console
    console.error('[deferredTelemetry] deferred init task failed', error);
  }
};

const scheduleIdle = (task: TelemetryTask): void => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => runGuarded(task), {
      timeout: IDLE_CALLBACK_TIMEOUT_MS,
    });
    return;
  }
  // WKWebView/Safari fallback — no idle signal available, so use a fixed
  // post-load delay that lands after hydration settles.
  window.setTimeout(() => runGuarded(task), NO_IDLE_CALLBACK_FALLBACK_DELAY_MS);
};

/**
 * Runs `task` once the page has loaded AND the main thread is idle.
 * SSR-safe no-op when `window` is undefined.
 */
export function deferTelemetryInit(task: TelemetryTask): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (document.readyState === 'complete') {
    scheduleIdle(task);
    return;
  }

  window.addEventListener('load', () => scheduleIdle(task), { once: true });
}
